import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type {
  CalendarMonth,
  DashboardStats,
  EaDetails,
  EaStatus,
  Group,
  HistoryPoint,
  NewsList,
  PushRules,
} from "../types";

export const REFRESH_INTERVAL_MS = 30_000;

export function useDashboardStats(groupId?: number | null) {
  return useQuery({
    queryKey: ["dashboard", groupId ?? null],
    queryFn: () =>
      apiFetch<DashboardStats>(`/api/dashboard${groupId ? `?groupId=${groupId}` : ""}`),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useEAs() {
  return useQuery({
    queryKey: ["eas"],
    queryFn: () => apiFetch<{ eas: EaStatus[] }>("/api/eas"),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useHiddenEAs(enabled: boolean) {
  return useQuery({
    queryKey: ["eas", "hidden"],
    queryFn: () => apiFetch<{ eas: EaStatus[] }>("/api/eas?hidden=true"),
    enabled,
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useHideEas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eaIds, hidden }: { eaIds: number[]; hidden: boolean }) =>
      apiFetch("/api/eas/hide", {
        method: "POST",
        body: JSON.stringify({ eaIds, hidden }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eas"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useResetMaxFloat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eaId: number) => apiFetch(`/api/eas/${eaId}/reset-max-float`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["eas"] }),
  });
}

/** The EA ingestion key, for the Settings prompt. Admin-only on the Worker; a 403 just means
 *  the prompt goes out without it. Not under ["eas"] so EA mutations don't refetch it. */
export function useEaIntegrationKey() {
  return useQuery({
    queryKey: ["integration-key"],
    queryFn: () => apiFetch<{ apiKey: string }>("/api/eas/integration-key"),
    staleTime: Infinity,
    retry: false,
  });
}

export function useEaDetails(id: string | undefined) {
  return useQuery({
    queryKey: ["eas", id],
    queryFn: () => apiFetch<EaDetails>(`/api/eas/${id}`),
    enabled: Boolean(id),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useEaHistory(id: string | undefined, limit = 2000) {
  return useQuery({
    queryKey: ["eas", id, "history", limit],
    queryFn: () => apiFetch<{ history: HistoryPoint[] }>(`/api/eas/${id}/history?limit=${limit}`),
    enabled: Boolean(id),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useGroups() {
  return useQuery({
    queryKey: ["groups"],
    queryFn: () => apiFetch<{ groups: Group[] }>("/api/groups"),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<{ group: { id: number; name: string } }>("/api/groups", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups"] }),
  });
}

export function useSetGroupExcluded() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, excludedFromAll }: { id: number; excludedFromAll: boolean }) =>
      apiFetch(`/api/groups/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ excludedFromAll }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/api/groups/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["eas"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useAssignGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eaIds, groupId }: { eaIds: number[]; groupId: number | null }) =>
      apiFetch("/api/groups/assign", {
        method: "POST",
        body: JSON.stringify({ eaIds, groupId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["eas"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** One month of the profit calendar ("YYYY-MM"). Past days never change, so unlike the
 *  live pages this doesn't poll; it refetches when the window regains focus after a minute. */
export function useProfitCalendar(month: string) {
  return useQuery({
    queryKey: ["calendar", month],
    queryFn: () => apiFetch<CalendarMonth>(`/api/calendar?month=${month}`),
    staleTime: 60_000,
  });
}

export function useNews() {
  return useQuery({
    queryKey: ["news"],
    queryFn: () => apiFetch<NewsList>("/api/news"),
    // The list only changes once a day (see the worker's daily cron), so this is just to
    // pick up reminders that have since been sent.
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useAddNewsEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (event: { title: string; country: string; eventTime: string }) =>
      apiFetch("/api/news", { method: "POST", body: JSON.stringify(event) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["news"] }),
  });
}

export function useDeleteNewsEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: number) => apiFetch(`/api/news/${eventId}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["news"] }),
  });
}

export function useAddNewsReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, minutesBefore }: { eventId: number; minutesBefore: number }) =>
      apiFetch(`/api/news/${eventId}/reminders`, {
        method: "POST",
        body: JSON.stringify({ minutesBefore }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["news"] }),
  });
}

export function useDeleteNewsReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reminderId: number) => apiFetch(`/api/news/reminders/${reminderId}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["news"] }),
  });
}

export function useVapidPublicKey() {
  return useQuery({
    queryKey: ["push", "vapid-public-key"],
    queryFn: () => apiFetch<{ publicKey: string }>("/api/push/vapid-public-key"),
    staleTime: Infinity,
  });
}

export function usePushRules(endpoint: string | undefined) {
  return useQuery({
    queryKey: ["push", "rules", endpoint],
    queryFn: () => apiFetch<{ rules: PushRules }>(`/api/push/rules?endpoint=${encodeURIComponent(endpoint!)}`),
    enabled: Boolean(endpoint),
    retry: false,
  });
}

export function useSubscribePush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
      apiFetch("/api/push/subscribe", {
        method: "POST",
        body: JSON.stringify(subscription),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["push"] }),
  });
}

export function useUnsubscribePush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (endpoint: string) =>
      apiFetch("/api/push/subscribe", {
        method: "DELETE",
        body: JSON.stringify({ endpoint }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["push"] }),
  });
}

export function useUpdatePushRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rules: {
      endpoint: string;
      enabled: boolean;
      floating_loss_threshold: number | null;
      margin_level_threshold: number | null;
      status_alerts: boolean;
      watch_all: boolean;
      group_ids: number[];
      ea_ids: number[];
    }) =>
      apiFetch("/api/push/rules", {
        method: "PUT",
        body: JSON.stringify(rules),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["push"] }),
  });
}

export function useTestPush() {
  return useMutation({
    mutationFn: (endpoint: string) =>
      apiFetch("/api/push/test", {
        method: "POST",
        body: JSON.stringify({ endpoint }),
      }),
  });
}
